import styled from "@emotion/styled/macro";
import {
  Button,
  SvgIcon,
  SvgNames,
  tokens,
  Typography,
} from "@sunrun/experience-ui-components";
import type { ReactNode } from "react";
import {
  DIRECT_TO_HOME,
  FUSION_CLOSER,
  FUSION_SETTER,
  type SplatUserType,
} from "@sunrun/sales-experience-shared";
import { useUserProfile } from "../../providers/UserProfile";
import { publishMessage } from "../../providers/Messages";
import salesSupportContactsImage from "../../assets/images/SalesSupportContacts.png";
import platformUserGuidesImage from "../../assets/images/PlatformUserGuides.png";
import runXLearningImage from "../../assets/images/RunXLearning.png";
import bugReportImage from "../../assets/images/BugReport.png";
import playbookImage from "../../assets/images/Playbook.png";

type HelpCenterOption = {
  title: string;
  description: ReactNode;
  image: string;
  buttonText: string;
  buttonLink: string;
  openInIframe?: boolean;
  requiredSplatRoles?: readonly SplatUserType[];
};

const OPTIONS: HelpCenterOption[] = [
  {
    title: "Sales support contacts",
    description: "Access to the support contacts for the sales process.",
    image: salesSupportContactsImage,
    buttonText: "Get Support",
    buttonLink:
      "https://current.sunrun.com/site/7b6848bd-8bfb-5e2e-b56d-41b94260487d/page/05f08d82-ad44-4fd7-b439-d5f8c5d632fe",
  },
  {
    title: "ONE User Guides",
    description:
      'Current has knowledge articles to teach you how to use ONE. Search any question you have with "in ONE" at the end, for instance "How do I upload a utility bill in ONE?"',
    image: platformUserGuidesImage,
    buttonText: "Learn ONE",
    buttonLink:
      "https://current.sunrun.com/site/7b6848bd-8bfb-5e2e-b56d-41b94260487d/content?type=page&pageCategoryId=5daf3824-a623-53e2-a6e8-06a6ec0120a0",
  },
  {
    title: "RunX Learning",
    description: "Access your required and optional RunX learning courses.",
    image: runXLearningImage,
    buttonText: "Go to RunX Learning",
    buttonLink: "https://sunrun.docebosaas.com/learn",
  },
  {
    title: "Bug Report",
    description:
      "Access to the help portal to report bugs or issues found in Sunrun’s software products.",
    image: bugReportImage,
    buttonText: "Report a Bug",
    buttonLink:
      "https://sunrun.service-now.com/help?id=sc_cat_item&sys_id=d0eab1f94fd84600170809d14210c7f3&sysparm_category=a3499ce4db570c10fb951e1339961972&spa=1",
  },
  {
    title: "Playbook",
    description: (
      <>
        Master the direct-to-home sales framework built by our company’s top
        producers.
        <br />
        <br />
        <strong>The Sales Cycle:</strong> Learn to navigate Prospecting,
        Discovery, Presentations, and Closing - with Objections covered at every
        stage.
        <br />
        <br />
        <strong>Active Training:</strong> Elevate your development through
        targeted Perspectives, practical Processes, and integrated Solo and
        Office Practice sessions.
        <br />
        <br />
        <strong>Your Living Copy:</strong> Take ownership of your career. Drop
        notes directly into the playbook during training; your entries auto-save
        instantly, creating your own personal, interactive copy.
      </>
    ),
    image: playbookImage,
    buttonText: "Playbook",
    buttonLink: "/support/playbook",
    openInIframe: true,
    requiredSplatRoles: [DIRECT_TO_HOME, FUSION_CLOSER, FUSION_SETTER] as const,
  },
];

const HelpCenter = () => {
  const userProfile = useUserProfile();

  const visibleOptions = OPTIONS.filter((option) => {
    if (!option.requiredSplatRoles?.length) return true;
    if (!userProfile?.splatRole) return false;
    return option.requiredSplatRoles.includes(
      userProfile.splatRole as SplatUserType
    );
  });

  const handleButtonClick = (option: HelpCenterOption) => {
    if (option.openInIframe) {
      publishMessage({
        source: "Support",
        type: "COMMAND:CHANGE_URL",
        payload: { url: option.buttonLink },
      });
      return;
    }

    window.open(option.buttonLink, "_blank");
  };

  return (
    <Wrapper>
      {visibleOptions.map((option) => (
        <UserGuideContainer key={option.title}>
          <UserGuideImage $type={option.image}></UserGuideImage>
          <UserGuideInfo>
            <Typography
              color={tokens.BRAND_HERO_BLUE}
              size={18}
              fontWeight="medium"
              lineHeight={21}
            >
              {option.title}
            </Typography>
            <Typography
              size={14}
              color={tokens.TINTS_OFF_WHITE_20}
              lineHeight={20}
            >
              {option.description}
            </Typography>
          </UserGuideInfo>
          <GoButton onClick={() => handleButtonClick(option)}>
            <Typography color={tokens.WHITE} size={16}>
              {option.buttonText}
            </Typography>
            <SvgIcon
              width={24}
              height={24}
              color={"white"}
              name={SvgNames.ExternalLink}
            />
          </GoButton>
        </UserGuideContainer>
      ))}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 598px);
  gap: 24px;

  @media (max-width: 1430px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const UserGuideImage = styled.div<{ $type: string }>`
  display: flex;
  border-radius: 8px;
  border: 1px solid ${tokens.TINTS_OFF_WHITE_40};
  background-image: url(${({ $type }) => $type});
  background-repeat: no-repeat;
  background-position: top left;
  background-size: cover;
  grid-area: image;
  width: 100%;
  height: 194px;
`;

const UserGuideContainer = styled.div`
  display: grid;
  grid-column-gap: 48px;
  grid-row-gap: 24px;
  grid-template-areas: "info image" "button image";
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: 1fr auto;
  padding: 20px 24px;
  border: solid 1px ${tokens.TINTS_OFF_WHITE_40}; //#D0CCC3
  border-radius: 16px;

  @media (max-width: 1430px) {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    flex: 1 0 0;
  }
`;

const UserGuideInfo = styled.div`
  grid-area: info;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex-grow: 1;
`;

const GoButton = styled(Button)`
  grid-area: button;
  width: 100%;
  line-height: 22px;
  padding: 8px 24px;
  font-weight: 400;

  & > span {
    display: flex;
    font-size: 16px;
    max-height: 24px;
    line-height: 24px;
    gap: 8px;
  }
`;

export { HelpCenter };
